// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import gitVersion from '../environments/git-version';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import store from './store';
import { DEFAULT_COMPONENT_ICONS } from '@azure/communication-react';
import { initializeIcons, registerIcons } from '@fluentui/react';
import InterviewHostAgent from './pages/AgentPage/InterviewHostAgent';
import JobsRoles from './pages/AgentPage/InterviewHostAgent/JobsRoles';
import AddCandidate from './pages/AgentPage/InterviewHostAgent/AddCandidate';
import Candidates from './pages/AgentPage/InterviewHostAgent/Candidates';
import InterviewReport from './pages/AgentPage/InterviewHostAgent/InterviewReport';
import SkillAssessmentReport from './pages/AgentPage/InterviewHostAgent/SkillAssessmentReport';
import Reports from './pages/AgentPage/InterviewHostAgent/Reports';
import Interview from './pages/Interview';
import NotFound from './pages/NotFound';

initializeIcons();
registerIcons({ icons: DEFAULT_COMPONENT_ICONS });

// Log SCM/build metadata on client startup
console.log(JSON.stringify(gitVersion));

// Protected route wrapper - redirects to SSO if not authenticated
const ProtectedRoute = ({ children }: { children: React.ReactElement }): React.ReactElement | null => {
  const [authChecked, setAuthChecked] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    fetch('/api/sso/status', { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        setIsAuthenticated(data.isAuthenticated);
        setAuthChecked(true);
        if (!data.isAuthenticated) {
          window.location.href = '/api/sso/start';
        }
      })
      .catch(() => {
        setAuthChecked(true);
        window.location.href = '/api/sso/start';
      });
  }, []);

  if (!authChecked) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? children : null;
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <Provider store={store}>
    <BrowserRouter basename="/app">
      <div className="App">
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <InterviewHostAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <JobsRoles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:jobId/candidates"
            element={
              <ProtectedRoute>
                <Candidates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:jobId/candidates/add"
            element={
              <ProtectedRoute>
                <AddCandidate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:jobId/candidates/:candidateId/report"
            element={
              <ProtectedRoute>
                <InterviewReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:jobId/candidates/:candidateId/skill-assessment-report"
            element={
              <ProtectedRoute>
                <SkillAssessmentReport />
              </ProtectedRoute>
            }
          />
          <Route path="/interview/:token" element={<Interview />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </BrowserRouter>
  </Provider>
);
